import { supabase } from '../lib/supabase';

import { toCamelCase, toSnakeCase } from './supabaseService';

// ============================================
// CONVERSATIONS
// ============================================

/**
 * Get all conversations for the current user
 */
export async function getUserConversations(userId = null) {
  try {
    // Dev mode bypass
    const isDevMode = import.meta.env?.VITE_DEV_MODE === 'true';
    let currentUserId = userId;

    if (!currentUserId) {
      if (isDevMode) {
        // Use mock user ID in dev mode
        currentUserId = '00000000-0000-0000-0000-000000000001';
      } else {
        const { data: { user }, error: authError } = await supabase?.auth?.getUser();
        if (authError || !user) throw new Error('User not authenticated');
        currentUserId = user?.id;
      }
    }

    const { data, error } = await supabase?.from('conversation_participants')?.select(`
        conversation_id,
        unread_count,
        last_read_at,
        conversations (
          id,
          type,
          title,
          last_message_at,
          created_at
        )
      `)?.eq('user_id', currentUserId)?.order('conversations(last_message_at)', { ascending: false });

    if (error) throw error;

    // Get other participants for each conversation
    const conversationsWithParticipants = await Promise.all(
      data?.map(async (item) => {
        const conversation = item?.conversations;
        
        // Get other participants
        const { data: participants } = await supabase?.from('conversation_participants')?.select(`
            user_id,
            user_profiles (
              id,
              full_name,
              avatar_url,
              email
            )
          `)?.eq('conversation_id', conversation?.id)?.neq('user_id', currentUserId);

        // Get last message
        const { data: lastMessage } = await supabase?.from('messages')?.select('content, created_at, sender_id')?.eq('conversation_id', conversation?.id)?.order('created_at', { ascending: false })?.limit(1)?.maybeSingle();

        return toCamelCase({
          ...conversation,
          unread_count: item?.unread_count,
          last_read_at: item?.last_read_at,
          participants: participants?.map(p => p?.user_profiles) || [],
          last_message: lastMessage
        });
      }) || []
    );

    return conversationsWithParticipants;
  } catch (error) {
    console.error('Error fetching conversations:', error);
    throw error;
  }
}

/**
 * Get or create a direct conversation between two users
 */
export async function getOrCreateConversation(otherUserId) {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    if (authError || !user) throw new Error('User not authenticated');

    // Check if conversation already exists
    const { data: existingConversations } = await supabase?.from('conversation_participants')?.select('conversation_id')?.eq('user_id', user?.id);

    if (existingConversations?.length > 0) {
      // Check if any of these conversations include the other user
      for (const conv of existingConversations) {
        const { data: otherParticipant } = await supabase?.from('conversation_participants')?.select('user_id')?.eq('conversation_id', conv?.conversation_id)?.eq('user_id', otherUserId)?.maybeSingle();

        if (otherParticipant) {
          // Conversation exists, return it
          const { data: conversation } = await supabase?.from('conversations')?.select('*')?.eq('id', conv?.conversation_id)?.single();

          return toCamelCase(conversation);
        }
      }
    }

    // Create new conversation
    const { data: newConversation, error: convError } = await supabase?.from('conversations')?.insert({ type: 'direct' })?.select()?.single();

    if (convError) throw convError;

    // Add participants
    const { error: participantsError } = await supabase?.from('conversation_participants')?.insert([
        { conversation_id: newConversation?.id, user_id: user?.id },
        { conversation_id: newConversation?.id, user_id: otherUserId }
      ]);

    if (participantsError) throw participantsError;

    return toCamelCase(newConversation);
  } catch (error) {
    console.error('Error getting/creating conversation:', error);
    throw error;
  }
}

/**
 * Mark conversation as read
 */
export async function markConversationAsRead(conversationId) {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    if (authError || !user) throw new Error('User not authenticated');

    const { error } = await supabase?.from('conversation_participants')?.update({ unread_count: 0, last_read_at: new Date()?.toISOString() })?.eq('conversation_id', conversationId)?.eq('user_id', user?.id);

    if (error) throw error;
  } catch (error) {
    console.error('Error marking conversation as read:', error);
    throw error;
  }
}

// ============================================
// MESSAGES
// ============================================

/**
 * Get messages for a conversation
 */
export async function getConversationMessages(conversationId, limit = 50) {
  try {
    const { data, error } = await supabase?.from('messages')?.select(`
        *,
        sender:user_profiles!messages_sender_id_fkey (
          id,
          full_name,
          avatar_url
        ),
        message_attachments (*)
      `)?.eq('conversation_id', conversationId)?.order('created_at', { ascending: true })?.limit(limit);

    if (error) throw error;

    return data?.map(msg => toCamelCase(msg)) || [];
  } catch (error) {
    console.error('Error fetching messages:', error);
    throw error;
  }
}

/**
 * Send a message
 */
export async function sendMessage(conversationId, content) {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    if (authError || !user) throw new Error('User not authenticated');

    const messageData = toSnakeCase({
      conversationId,
      senderId: user?.id,
      content,
      status: 'sent'
    });

    const { data, error } = await supabase?.from('messages')?.insert(messageData)?.select(`
        *,
        sender:user_profiles!messages_sender_id_fkey (
          id,
          full_name,
          avatar_url
        )
      `)?.single();

    if (error) throw error;

    return toCamelCase(data);
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
}

/**
 * Update message status (delivered/read)
 */
export async function updateMessageStatus(messageId, status) {
  try {
    const { error } = await supabase?.from('messages')?.update({ status })?.eq('id', messageId);

    if (error) throw error;
  } catch (error) {
    console.error('Error updating message status:', error);
    throw error;
  }
}

/**
 * Delete a message
 */
export async function deleteMessage(messageId) {
  try {
    const { error } = await supabase?.from('messages')?.delete()?.eq('id', messageId);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting message:', error);
    throw error;
  }
}

// ============================================
// REAL-TIME SUBSCRIPTIONS
// ============================================

/**
 * Subscribe to new messages in a conversation
 */
export function subscribeToMessages(conversationId, callback) {
  const channel = supabase?.channel(`messages:${conversationId}`)?.on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'messages',
        filter: `conversation_id=eq.${conversationId}`
      },
      async (payload) => {
        // Fetch full message with sender details
        const { data } = await supabase?.from('messages')?.select(`
            *,
            sender:user_profiles!messages_sender_id_fkey (
              id,
              full_name,
              avatar_url
            )
          `)?.eq('id', payload?.new?.id)?.single();

        if (data) {
          callback({ type: 'INSERT', message: toCamelCase(data) });
        }
      }
    )?.on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'messages',
        filter: `conversation_id=eq.${conversationId}`
      },
      async (payload) => {
        // Fetch full message with sender details
        const { data } = await supabase?.from('messages')?.select(`
            *,
            sender:user_profiles!messages_sender_id_fkey (
              id,
              full_name,
              avatar_url
            )
          `)?.eq('id', payload?.new?.id)?.single();

        if (data) {
          callback({ type: 'UPDATE', message: toCamelCase(data) });
        }
      }
    )?.subscribe();

  return channel;
}

/**
 * Subscribe to conversation updates
 */
export function subscribeToConversations(userId, callback) {
  const channel = supabase?.channel('conversations_updates')?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'conversations'
      },
      (payload) => {
        callback(toCamelCase(payload?.new || payload?.old));
      }
    )?.subscribe();

  return channel;
}

/**
 * Subscribe to user online status via presence
 */
export function subscribeToUserPresence(conversationId, userId, callback) {
  const channel = supabase?.channel(`presence:conversation:${conversationId}`, {
    config: {
      presence: {
        key: userId
      }
    }
  });

  // Track presence state
  channel?.on('presence', { event: 'sync' }, () => {
    const state = channel?.presenceState();
    const onlineUsers = Object.keys(state);
    callback({ type: 'PRESENCE_SYNC', onlineUsers });
  })?.on('presence', { event: 'join' }, ({ key }) => {
    callback({ type: 'USER_JOINED', userId: key });
  })?.on('presence', { event: 'leave' }, ({ key }) => {
    callback({ type: 'USER_LEFT', userId: key });
  })?.subscribe(async (status) => {
    if (status === 'SUBSCRIBED') {
      // Track current user as online
      await channel?.track({
        userId,
        onlineAt: new Date()?.toISOString()
      });
    }
  });

  return channel;
}

/**
 * Unsubscribe from channel
 */
export function unsubscribeChannel(channel) {
  if (channel) {
    supabase?.removeChannel(channel);
  }
}

// ============================================
// FILE ATTACHMENTS
// ============================================

/**
 * Upload message attachment
 */
export async function uploadMessageAttachment(file, messageId) {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    if (authError || !user) throw new Error('User not authenticated');

    const fileExt = file?.name?.split('.')?.pop();
    const fileName = `${user?.id}/${Date.now()}-${Math.random()?.toString(36)?.substring(7)}.${fileExt}`;

    const { data: uploadData, error: uploadError } = await supabase?.storage?.from('message-attachments')?.upload(fileName, file);

    if (uploadError) throw uploadError;

    // Create attachment record
    const { data: attachment, error: attachmentError } = await supabase?.from('message_attachments')?.insert({
        message_id: messageId,
        file_path: uploadData?.path,
        file_name: file?.name,
        file_type: file?.type,
        file_size: file?.size
      })?.select()?.single();

    if (attachmentError) throw attachmentError;

    return toCamelCase(attachment);
  } catch (error) {
    console.error('Error uploading attachment:', error);
    throw error;
  }
}

/**
 * Get signed URL for attachment
 */
export async function getAttachmentUrl(filePath) {
  try {
    const { data, error } = await supabase?.storage?.from('message-attachments')?.createSignedUrl(filePath, 3600); // 1 hour expiry

    if (error) throw error;

    return data?.signedUrl;
  } catch (error) {
    console.error('Error getting attachment URL:', error);
    throw error;
  }
}

/**
 * Delete attachment
 */
export async function deleteAttachment(attachmentId, filePath) {
  try {
    // Delete from storage
    const { error: storageError } = await supabase?.storage?.from('message-attachments')?.remove([filePath]);

    if (storageError) throw storageError;

    // Delete record
    const { error: dbError } = await supabase?.from('message_attachments')?.delete()?.eq('id', attachmentId);

    if (dbError) throw dbError;
  } catch (error) {
    console.error('Error deleting attachment:', error);
    throw error;
  }
}
function messagingService(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: messagingService is not implemented yet.', args);
  return null;
}

export { messagingService };